import { MCP_LIBRARY_TESTS_PER_MINUTE } from "./mcp-library.js";
// Limits (V6 plan 13.3.1, row H113, decision D42's defaults), declared once: the server builds
// its windows from these numbers and the OpenAPI document (openapi.ts) describes them, the 429
// and its headers included.
//
// - Windows. Fixed one-minute windows, in memory: valid while one replica serves a site (D30).
//   Every read a caller makes with a credential spends one read window of that credential
//   (READ_LIMIT_PER_MINUTE): the cookie, a bearer session, or one account key, so two keys of
//   one account, and a key and the cookie, never share it. The public routes (the catalog
//   snapshot, the OpenAPI document) read no credential and spend nothing, nor does a public
//   procedure called with none. The writes that spend (WRITE_WINDOWS) keep their own windows,
//   per account across all its credentials, as before; identity.exchange's is per client IP.
// - A refusal is 429 `rate_limited` with RateLimitedData, `Retry-After` (seconds until its
//   window turns) and the `RateLimit-*` headers (RATE_LIMIT_HEADERS). An answer under a window
//   carries the `RateLimit-*` headers too, describing the call's own write window where it has
//   one (runs.preflight's), else the read window.
// - Open runs. An account key holds at most KEY_OPEN_RUNS_MAX runs whose status is not
//   terminal: runs.start and runs.replay by a key with that many open, and a runs.command
//   message that would reopen a finished run the key started, are 429 `key_open_runs_exceeded`
//   with KeyOpenRunsExceededData and no `Retry-After`, since no window turns: the caller waits
//   for one of the key's runs to end, or stops one. The server admits each such run under a
//   per-key lock in the transaction that opens it, so calls sent together never pass the cap
//   together. The cookie and bearer sessions are not capped.
// - Runs a day, per account (plan row H.66, decision D66). An account's plan names a
//   `dailyRunCap` (plans.ts; null = unlimited): the account's run rows created in the rolling
//   DAILY_RUN_CAP_WINDOW_MS ending now, every status, replays included, whoever started them.
//   Once that many exist, runs.start and runs.replay are 422 `daily_run_cap_reached` with
//   DailyRunCapReachedData: a start blocker, so runs.preflight reports it (ready: false) and
//   the CLI exits 2. A follow-up message on a finished run is not a start and is not counted.
/** 429: a window is spent. Its data is RateLimitedData. */
export const RATE_LIMITED = "rate_limited";
/** 429: the key holds KEY_OPEN_RUNS_MAX open runs. Its data is KeyOpenRunsExceededData. */
export const KEY_OPEN_RUNS_EXCEEDED = "key_open_runs_exceeded";
/** 422: the account started its plan's dailyRunCap runs in the window. Its data is DailyRunCapReachedData. */
export const DAILY_RUN_CAP_REACHED = "daily_run_cap_reached";
/** Reads a minute per credential. */
export const READ_LIMIT_PER_MINUTE = 600;
/** Runs one account key may hold whose status is not terminal (TERMINAL_RUN_STATUS_LIST). */
export const KEY_OPEN_RUNS_MAX = 5;
/** Every window's length. */
export const RATE_WINDOW_MS = 60_000;
/** The rolling window a plan's dailyRunCap counts over: the 24 hours ending now. */
export const DAILY_RUN_CAP_WINDOW_MS = 24 * 3_600_000;
/** The write procedures that spend a window, by window. */
export const WRITE_WINDOWS = {
    /** A replay is a start: the same window. */
    start: { limit: 30, per: "account", procedures: ["runs.start", "runs.replay"] },
    command: { limit: 120, per: "account", procedures: ["runs.command"] },
    setBudget: { limit: 30, per: "account", procedures: ["runs.setBudget"] },
    /** Reads in their access, so each spends the read window too; validate checks a candidate on every pair in one call. */
    preflight: { limit: 60, per: "account", procedures: ["runs.preflight", "projects.validate"] },
    apiKeyCreate: { limit: 10, per: "account", procedures: ["apiKeys.create"] },
    mcpLibraryTest: {
        limit: MCP_LIBRARY_TESTS_PER_MINUTE,
        per: "account",
        procedures: ["mcpLibrary.test"],
    },
    /** The sign-in brake: an address minting sessions or probing with junk tokens. */
    exchange: { limit: 30, per: "client_ip", procedures: ["identity.exchange"] },
};
/** The procedures an open-run cap holds. */
export const KEY_OPEN_RUNS_PROCEDURES = ["runs.start", "runs.replay"];
/** A window's length as RateLimitedData names it: `1m` for a minute. */
export function windowLabel(windowMs) {
    if (windowMs % 3_600_000 === 0)
        return `${windowMs / 3_600_000}h`;
    if (windowMs % 60_000 === 0)
        return `${windowMs / 60_000}m`;
    return `${Math.ceil(windowMs / 1000)}s`;
}
/**
 * The response headers of a window (IETF draft httpapi-ratelimit-headers, the separate fields),
 * by name as the server sends them, with what each means: what the OpenAPI document declares.
 */
export const RATE_LIMIT_HEADERS = {
    "Retry-After": "On a 429 rate_limited: whole seconds until the window turns, the same as data.retryAfterSeconds. Not sent with key_open_runs_exceeded, which no window turns.",
    "RateLimit-Limit": "Calls the window allows: its limit per minute.",
    "RateLimit-Remaining": "Calls left in the window; 0 once it is spent.",
    "RateLimit-Reset": "Whole seconds until the window turns.",
};
//# sourceMappingURL=limits.js.map